import { ComponentType, useContext, useEffect, useState } from 'react';
import {
  Window,
  WindowActionsBar,
  WindowProps
} from '@progress/kendo-react-dialogs';
import { FluidForm } from '../../../components/forms';
import { TaskContentEntity } from '../../../types/game-document/';
import { Button } from '@progress/kendo-react-buttons';
import { EditorMode } from '../../../types/editor-mode';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { TaskContentEditor } from './task-content-editor';
import { GameDocumentContext } from '../../../contexts/game-document';
import RequiredFields from '../../../types/required-fields';
import { ERR_INPUT_REQUIRED } from '../../../constants/text';
import { GetResourceValue } from '../../../utils/game-document';

interface TaskContentEditorWindowProps extends WindowProps {
  editorEntity: EntityEditor<TaskContentEntity>;
  toggleDialog: Function;
  onSubmit: (
    editorEntity: EntityEditor<TaskContentEntity>,
    createTask?: boolean,
    title?: string
  ) => void;
  editorMode: EditorMode;
}

export const TaskContentEditorWindow: ComponentType<
  TaskContentEditorWindowProps
> = ({
  editorEntity,
  toggleDialog,
  onSubmit,
  editorMode,
  ...props
}: TaskContentEditorWindowProps) => {
  const [entity, setEntity] = useState({ ...editorEntity.entity });
  const [state] = useContext(GameDocumentContext);
  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TaskContentEntity>[]
  >([{ name: 'title', errorMessage: '' }]);
  const [createTask, setCreateTask] = useState(true);
  const [title, setTitle] = useState(
    editorEntity.isNew
      ? ''
      : GetResourceValue(
          state.gameDocument!,
          editorEntity.entity.titleResId ?? ''
        )
  );

  const handleEntityChange = (keyEntity: string, value: string) => {
    setEntity({ ...entity, [keyEntity]: value });
  };

  const isInputValid = (): boolean => {
    const inputValidation = requiredFields.map((input) => {
      input.errorMessage = '';

      if (input.name === 'title') {
        if (title === '') {
          input.errorMessage = ERR_INPUT_REQUIRED;
        }
      }

      return input;
    });
    setRequiredFields(inputValidation);
    return (
      inputValidation.findIndex((input) => input.errorMessage !== '') === -1
    );
  };

  useEffect(() => {
    if (entity.bodyType === undefined) {
      setEntity({ ...entity, bodyType: 'HTML' });
    }
  }, []);

  return (
    <>
      <Window
        modal={true}
        minimizeButton={undefined}
        className={'shadow'}
        initialWidth={500}
        initialHeight={470}
        title={
          editorEntity.isNew
            ? 'New task content'
            : GetResourceValue(
                state.gameDocument!,
                editorEntity.entity.titleResId ?? ''
              )
        }
        {...props}>
        <FluidForm>
          <TaskContentEditor
            editorMode={editorMode}
            entity={entity}
            requiredFields={requiredFields}
            handleEntityChange={handleEntityChange}
            isNew={editorEntity.isNew}
            createTask={createTask}
            onCreateTaskChange={setCreateTask}
            title={title}
            onTitleChange={setTitle}
          />
        </FluidForm>
        <WindowActionsBar>
          <Button themeColor={'secondary'} onClick={() => toggleDialog()}>
            Cancel
          </Button>
          <Button
            themeColor={'primary'}
            onClick={() => {
              if (isInputValid()) {
                onSubmit(
                  { isNew: editorEntity.isNew, entity },
                  createTask,
                  title
                );
              }
            }}>
            Save
          </Button>
        </WindowActionsBar>
      </Window>
    </>
  );
};
